import pdpConfig from '@/config/property';
import reviewsConfig from '@/config/reviews';
import { useCurrencyStore } from '@/stores/currency';

export function useApiProperties() {
  const {
    $api,
    $i18n,
  } = useNuxtApp();
  const { locale } = $i18n;

  const currencyStore = useCurrencyStore();
  const testingStore = useTestingStore();

  const dateTime = useDateTime();
  const { useImage } = useImages();

  const userCurrency = currencyStore.getCurrentCurrency;

  const defaultQuery = { currency: userCurrency?.code };

  const defaultHeaders = { 'Accept-Language': locale?.value };

  const defaultParams = {};

  const getPropertyHouseRules = async (propertyId) => {
    if (!/^\d+$/.test(String(propertyId))) {
      return null;
    }

    try {
      const callKey = `getPropertyHouseRules${locale?.value}${propertyId}`;
      const property = await $api.msapi(
        callKey,
        `property/${propertyId}/`,
        defaultHeaders,
        defaultQuery,
        defaultParams,
      );
      return property?.houseRules || null;
    } catch (error) {
      console.error(`### Error fetching house rules for property ${propertyId}:`, error);
      return null;
    }
  };

  const formulateAuthorRank = (numberOfReviews) => {
    if (numberOfReviews <= reviewsConfig.rankLevel.novice) {
      return reviewsConfig.rank.novice;
    }

    if (numberOfReviews <= reviewsConfig.rankLevel.avid) {
      return reviewsConfig.rank.avid;
    }

    return reviewsConfig.rank.globe;
  };

  const calculatePropertyOverallRating = (ratingTotal) => {
    if (ratingTotal <= 0 || ratingTotal > 100) {
      return 0;
    }
    return Number((ratingTotal / 10).toFixed(1));
  };

  const formatPropertyReviewResponse = (reviewResponse) => {
    return {
      pagination: reviewResponse.pagination,
      reviews: reviewResponse.reviews.map((review) => ({
        overallRating: calculatePropertyOverallRating(review?.rating?.overall),
        reviewCard: {
          ownerComment: review?.ownerComment,
          reviewDate: dateTime.formatToHumanReadableShortDate(review?.date, review?.languageCode, 'month|year'),
          reviewText: review?.notes,
        },
        author: {
          gender: `${review?.groupInformation?.groupTypeCode[0].toUpperCase()}${review?.groupInformation?.groupTypeCode.slice(1).toLowerCase()}`,
          nickname: review?.user?.nickname,
          numberOfReviews: review?.user?.numberOfReviews,
          rank: formulateAuthorRank(review?.user?.numberOfReviews),
          countryCode: review?.user?.nationality?.code,
          countryName: review?.user?.nationality?.name,
          ageRange: review?.groupInformation?.age,
        },
      })),
    };
  };

  const handlePropertyHouseRules = (propertyPolicies) => {
    const newHouseRules = [];
    const additionalPolicies = propertyPolicies.additional || [];
    const {
      smoking: smokingPolicy,
      pets: petsPolicy,
      curfew: curfewPolicy,
      children: childrenPolicy,
      ageLimit: ageLimitPolicy,
    } = propertyPolicies;

    ['TAXESINCLUDED', 'TAXESNOTINCLUDED'].forEach((rule) => {
      if (additionalPolicies.includes(rule)) {
        newHouseRules.push({ title: rule });
      }
    });

    // Handle age limit policy (new house rules)
    if (ageLimitPolicy?.ageRestriction === true) {
      let ageLimitSubtitleKey = null;
      let ageLimitSubtitleParams = null;
      if (Number.isInteger(ageLimitPolicy?.from) && Number.isInteger(ageLimitPolicy?.to)) {
        ageLimitSubtitleKey = 't_HOUSERULESAGELIMITRANGE';
        ageLimitSubtitleParams = {
          FROM: ageLimitPolicy.from,
          TO: ageLimitPolicy.to,
        };
      } else if (Number.isInteger(ageLimitPolicy?.from)) {
        ageLimitSubtitleKey = 't_HOUSERULESAGELIMITMINAGE';
        ageLimitSubtitleParams = { FROM: ageLimitPolicy.from };
      } else if (Number.isInteger(ageLimitPolicy?.to)) {
        ageLimitSubtitleKey = 't_HOUSERULESAGELIMITMAXAGE';
        ageLimitSubtitleParams = { TO: ageLimitPolicy.to };
      }

      newHouseRules.push({
        icon: 'restriction',
        title: 'HOUSERULESAGELIMITALLOWED',
        subtitleKey: ageLimitSubtitleKey,
        subtitleParams: ageLimitSubtitleParams,
      });
    }

    // Handle children policy (new house rules)
    if (typeof childrenPolicy?.allowChildren === 'boolean') {
      let childrenSubtitleKey = null;
      let childrenSubtitleParams = null;

      if (childrenPolicy.allowChildren) {
        if (Number.isInteger(childrenPolicy?.from) && Number.isInteger(childrenPolicy?.to)) {
          childrenSubtitleKey = 't_HOUSERULESCHILDRENALLOWEDRANGE';
          childrenSubtitleParams = {
            FROM: childrenPolicy.from,
            TO: childrenPolicy.to,
          };
        } else if (Number.isInteger(childrenPolicy?.from)) {
          childrenSubtitleKey = 't_HOUSERULESCHILDRENALLOWEDMINAGE';
          childrenSubtitleParams = { FROM: childrenPolicy.from };
        } else if (Number.isInteger(childrenPolicy?.to)) {
          childrenSubtitleKey = 't_HOUSERULESCHILDRENALLOWEDMAXAGE';
          childrenSubtitleParams = { TO: childrenPolicy.to };
        }
      }

      newHouseRules.push({
        icon: childrenPolicy.allowChildren ? 'children' : 'no-children',
        title: childrenPolicy.allowChildren ? 'HOUSERULESCHILDRENALLOWED' : 'HOUSERULESCHILDRENNOTALLOWED',
        subtitleKey: childrenSubtitleKey,
        subtitleParams: childrenSubtitleParams,
      });
    }

    ['CREDITCARDSACCEPTED', 'CREDITCARDSNOTACCEPTED'].forEach((rule) => {
      if (additionalPolicies.includes(rule)) {
        newHouseRules.push({ title: rule });
      }
    });

    // Handle curfew policy (new house rules)
    if (typeof curfewPolicy?.enabled === 'boolean') {
      const hasCurfewHours = curfewPolicy.enabled && curfewPolicy.from && curfewPolicy.to;
      const curfewRule = {
        icon: curfewPolicy.enabled ? 'clock' : 'no-curfew',
        title: curfewPolicy.enabled ? 'HOUSERULESCURFEWALLOWED' : 'HOUSERULESCURFEWNOTALLOWED',
      };

      if (hasCurfewHours) {
        curfewRule.subtitleKey = 't_HOUSERULESCURFEWEXPLANATION';
        curfewRule.subtitleParams = {
          FROM: curfewPolicy.from,
          TO: curfewPolicy.to,
        };
      } else {
        curfewRule.subtitle = null;
      }

      newHouseRules.push(curfewRule);
    }

    // Handle smoking policy (new house rules)
    if (Number.isInteger(smokingPolicy)) {
      if (smokingPolicy === 0) {
        newHouseRules.push({
          icon: 'no-smoke',
          title: 'HOUSERULESNOSMOKING',
        });
      } else if (smokingPolicy === 1 || smokingPolicy === 2) {
        newHouseRules.push({
          icon: 'smoking-area',
          title: 'HOUSERULESSMOKING',
          subtitleKey: smokingPolicy === 1
            ? 't_HOUSERULESSMOKINGANYWHERE'
            : 't_HOUSERULESSMOKINGINDESIGNATEDAREAS',
        });
      }
    }

    // Handle pets policy (new house rules)
    if (typeof petsPolicy?.allowed === 'boolean') {
      newHouseRules.push({
        icon: petsPolicy.allowed ? 'pet' : 'no-pet',
        title: petsPolicy.allowed ? 'HOUSERULESPETSALLOWED' : 'HOUSERULESPETSNOTALLOWED',
      });
    }

    return newHouseRules;
  };

  const getPropertyByPropertyId = async (propertyId, extraInfo = true) => {
    let extraPropertyInfo = {};

    // Trust boundary: propertyId comes from the URL and is concatenated into the internal
    // SPAPI path, so anything non-numeric (e.g. encoded "../") must be rejected before any
    // API call. 404 keeps crawler and redirection semantics of a not-found property.
    if (!/^\d+$/.test(String(propertyId))) {
      throw createError({
        statusCode: 404,
        statusMessage: '### Invalid property ID format',
      });
    }

    try {
      const callKeySpapi = `getPropertyByPropertyId${locale?.value}${propertyId}${userCurrency?.code}`;
      const endpointSpapi = `property/${propertyId}/`;
      const querySpapi = {
        ...defaultQuery,
        allowInactive: 1,
      };
      // surfaceError: transient spapi failures must throw (not resolve null) so the catch
      // below maps them to a 503 instead of a not-found 404 (Sentry - STATICPWA-V3-1C0).
      const resultSpapi = await $api.spapi(callKeySpapi, endpointSpapi, defaultHeaders, querySpapi, defaultParams, { surfaceError: true });
      const propertyInfo = resultSpapi?.data || {};

      // Validate that we received basic property information
      if (!propertyInfo?.id) {
        const errorDescription = `### API returned invalid property data for ID ${propertyId}`;
        throw createError({
          statusCode: 404,
          statusMessage: errorDescription,
        });
      }

      // Validate that the property has location data
      if (!propertyInfo?.location) {
        console.error(`### Property ${propertyId} missing location data:`, propertyInfo);
      }

      if (propertyInfo?.id && extraInfo) {
        const extraApiCalls = [];

        const experimentSocialCuesParticipating = testingStore.hasEnabledFeature(
          'web_isUserParticipatingSocialCuesCallouts',
        );
        const isEnabledNewHouseRules = testingStore.hasEnabledFeature(
          'static_newHouseRules',
        );

        if (isEnabledNewHouseRules && propertyInfo.policies) {
          propertyInfo.policies.rules = handlePropertyHouseRules(propertyInfo.policies);
        }

        // LINKUPS API CALL DATA
        const linkupsVars = testingStore.getFeatureVariables('web_linkupsCallouts');
        const linkupsNumDays = linkupsVars?.numDays || 7;
        const linkupsStartDate = dateTime.formatToFlat(dateTime.today);
        const linkupsEndDate = dateTime.formatToFlat(dateTime.addDaysToDate(dateTime.today, linkupsNumDays));
        const callKeyLinkupsApi = `getPropertyLinkupsEvents${locale?.value}${propertyId}${userCurrency?.code}${linkupsStartDate}${linkupsEndDate}`;
        const endpointLinkupsApi = `public/properties/${propertyId}/hostel-gatherings/`;
        const queryLinksupsApi = {
          page: 1,
          pageSize: linkupsVars?.maxNumEvents || 8,
          startDate: linkupsStartDate,
          endDate: linkupsEndDate,
        };
        extraApiCalls.push({
          key: 'linkups',
          apiCall: () =>
            $api.linkupsApi(callKeyLinkupsApi, endpointLinkupsApi, defaultHeaders, queryLinksupsApi, defaultParams),
        });

        const experimentPdpOgEvents = testingStore.hasEnabledFeature('web_pdpOGEventsCarousel');
        const cityId = propertyInfo?.location?.city?.id;

        if (experimentPdpOgEvents && locale?.value === 'en' && cityId) {
          // OG GATHERING API CALL DATA
          const ogGatheringsStartDate = dateTime.formatToFlat(dateTime.today);
          const ogGatheringsEndDate = dateTime.formatToFlat(dateTime.addDaysToDate(dateTime.today, 5));
          const callKeyOgGatheringsApi = `getCityOgGatherings${locale?.value}${cityId}${userCurrency?.code}${ogGatheringsStartDate}${ogGatheringsEndDate}`;
          const endpointOgGatheringsApi = `public/cities/${cityId}/og-gatherings`;
          const queryOgGatheringsApi = {
            page: 1,
            pageSize: 50,
            startDate: ogGatheringsStartDate,
            endDate: ogGatheringsEndDate,
          };
          extraApiCalls.push({
            key: 'ogGatherings',
            apiCall: () =>
              $api.linkupsApi(
                callKeyOgGatheringsApi,
                endpointOgGatheringsApi,
                defaultHeaders,
                queryOgGatheringsApi,
                defaultParams,
              ),
          });
        }

        if (experimentSocialCuesParticipating) {
          // SOCIAL CUES API CALL DATA
          const socialCuesStartDate = dateTime.tomorrow;
          const socialCuesEndDate = dateTime.addDaysToDate(socialCuesStartDate, 3);
          const callKeySocialCuesApi = `getPropertySocialCues${locale?.value}${propertyId}${userCurrency?.code}`;
          const endpointSocialCuesApi = 'social-cues/single-property/';
          const querySocialCuesApi = {
            propertyId,
            from: dateTime.formatToFlat(socialCuesStartDate, 'dd-MM-yyyy'),
            to: dateTime.formatToFlat(socialCuesEndDate, 'dd-MM-yyyy'),
            number_of_results: pdpConfig.socialCues.limitAvatarsForPDP,
          };
          extraApiCalls.push({
            key: 'socialcues',
            apiCall: () =>
              $api.tpapi(
                callKeySocialCuesApi,
                endpointSocialCuesApi,
                defaultHeaders,
                querySocialCuesApi,
                defaultParams,
              ),
          });
        }

        // MSAPI CALL DATA
        const callKeyMsApi = `getPropertyByPropertyIdMsApi${locale?.value}${propertyId}${userCurrency?.code}`;
        const endpointMsApi = `property/${propertyId}/`;
        extraApiCalls.push({
          key: 'msapi',
          apiCall: () => $api.msapi(callKeyMsApi, endpointMsApi, defaultHeaders, defaultQuery, defaultParams),
        });

        // CALL ALL OTHER NECESSARY APIs AT THE SAME TIME AND WAIT FOR THE SLOWEST ONE...
        try {
          const extraApiCallsResults = await Promise.allSettled(extraApiCalls.map((c) => c.apiCall()));

          // ... AND THEN MAP THE RESULTS TO THE CORRECT API CALL
          const extraApiCallsData = extraApiCallsResults.reduce((acc, result, index) => {
            acc[extraApiCalls[index].key] = toValue(result?.value) || {};
            return acc;
          }, {});

          const propertyMsApiInfo = extraApiCallsData?.msapi || {};
          const propertyLinkupsApiInfo = extraApiCallsData?.linkups || {};
          const propertyOgGatheringsApiInfo = extraApiCallsData?.ogGatherings || {};
          const propertySocialCuesApiInfo = extraApiCallsData?.socialcues || {};

          const propertySocialCuesUsers = propertySocialCuesApiInfo?.data?.map((user) => {
            return {
              picture: useImage.getOptimizedImageUrl(user?.profilePictureUrl, {
                width: 32,
                height: 32,
              }),
              flag: user?.countryCode ? `https://a.hwstatic.com/hw/flags/${user.countryCode}.svg` : null,
              name: '',
            };
          });

          extraPropertyInfo = {
            isNew: propertyMsApiInfo.isNew,
            videos: propertyMsApiInfo?.videos,
            recommendedByHw: propertyMsApiInfo?.hostelworldRecommends,
            hoscars: propertyMsApiInfo?.hoscars,
            hostelworldSays: propertyMsApiInfo?.hostelworldSays,
            facilities: propertyMsApiInfo?.facilities,
            depositPercentage: propertyMsApiInfo.depositPercentage,
            currency: propertyMsApiInfo.currency,
            linkups: propertyLinkupsApiInfo,
            ogGatherings: propertyOgGatheringsApiInfo,
            whosGoing: {
              users: propertySocialCuesUsers || [],
              total: propertySocialCuesApiInfo?.total || 0,
            },
            businessNumber: propertyMsApiInfo?.businessNumber,
            // ... add more fields here from HWAPI or MSAPI as needed
          };
        } catch (extraApiError) {
          console.error(`### Error fetching extra property data for ID ${propertyId}:`, extraApiError);
          // Continue with basic property info if extra APIs fail
        }
      }

      return {
        ...propertyInfo,
        ...extraPropertyInfo,
      };
    } catch (error) {
      console.error(`### Error in getPropertyByPropertyId for ID ${propertyId}`);

      // (Sentry - STATICPWA-V3-1C0)A confirmed not-found stays a 404 so crawlers de-index the page;
      // Any other failure is transient and must be a 503 so crawlers retry instead of dropping the page.
      if (error?.statusCode === 404) {
        throw error;
      }

      throw createError({
        statusCode: 503,
        statusMessage: `### Failed to fetch property ${propertyId}`,
        cause: error,
      });
    }
  };

  const getPropertyReviews = async (propertyId, filters) => {
    const callKey = `getPropertyReviews${locale?.value}${propertyId}${userCurrency?.code}${filters.sort}${filters.allLanguages}${filters.page}`;
    const endpoint = `properties/${propertyId}/reviews/`;

    const callParams = {
      ...defaultParams,
      ...filters,
    };

    const result = await $api.hwapi(callKey, endpoint, defaultHeaders, defaultQuery, callParams);

    return formatPropertyReviewResponse(result) || null;
  };

  return {
    getPropertyByPropertyId,
    getPropertyHouseRules,
    getPropertyReviews,
  };
}
